<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>
        <div
            v-if="loading || adding || removing"
            key="loading"
        >
            <b-spinner small></b-spinner>
        </div>
        <ul 
            v-else
            key="notLoading"
            class="mb-0 list-flush"
        >
            <li
                v-for="tu in invitedTus"
                :key="tu.id"
            >
                <div class="d-flex justify-content-start align-items-center">
                    <div>{{ tu.full_name }}</div>
                    <b-button
                        size="sm"
                        variant="bare-danger"
                        class="ml-3"
                        @click="removeTu(tu)"
                        :disabled="currentUserInfo.tenant_user_id === tu.id"
                    >Remove</b-button>
                </div>
            </li>
        </ul>
        <div class="mt-3">
            <b-button
                variant="gray-primary"
                @click="addTuModal = true"
            >
                <b-icon icon="person-plus" class="mr-2"></b-icon>
                Add Team Member
            </b-button>
        </div>

        <b-modal
            v-model="addTuModal"
            title="Add Team Members"
            centered
            size="md"
            footer-class="border-0"
            ok-title="Done"
            @show="getCompanyTus"
        >
            <b-table
                :items="allTus"
                :fields="fields"
                :busy="loadingAllTus"
                class="no-top-border"
            >
                <template #table-busy>
                    <div class="text-center">
                        <b-spinner></b-spinner>
                    </div>
                </template>
                <template #cell(actions)="data">
                    <div v-if="adding || removing" key="adding">
                        <b-spinner small></b-spinner>
                    </div>
                    <div v-else key="notAdding">
                        <b-button
                            v-if="isInvited(data.item)"
                            key="invited"
                            variant="gray-danger"
                            size="sm"
                            :disabled="currentUserInfo.tenant_user_id === data.item.id"
                            @click="removeTu(data.item)"
                        >
                            <b-icon icon="person-dash" class="mr-1"></b-icon>
                            Remove
                        </b-button>
                        <b-button
                            v-else
                            key="notInvited"
                            variant="gray-primary"
                            size="sm"
                            @click="addTu(data.item.id)"
                        >
                            <b-icon icon="person-plus" class="mr-1"></b-icon>
                            Add
                        </b-button>
                    </div>
                </template>
            </b-table>

            <div class="text-center mt-3">
                Need to add a new user to your company?
                <b-link :href="splUrlMap.company_users_update">Manage Company Users</b-link>
            </div>

        </b-modal>
    
    </div>
</template>

<script>
import { suppliosApi } from '../js/api-service';
import { splUrlMapMixin, currentUserInfoMixin } from '../js/custom-vue-mixins';

export default {
    name: 'VendorRfxResponseInvitedTus',
    components: {},
    mixins: [
        splUrlMapMixin,
        currentUserInfoMixin,
    ],
    props: {
        responseId: {
            type: [String, Number],
            required: true,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            responseData: {},  // full data for this rfxResponse from API
            invitedTus: [],  // pull out invited_tus and put here
            loadingAllTus: false, 
            addTuModal: false,  // v-model to show the Modal
            adding: false,  
            removing: false,
            allTus: [],  // all TUs for the company go here
            fields: [
                {
                    key: 'full_name',
                    label: 'Name',
                    tdClass: 'font-weight-bold',
                },
                {
                    key: 'email',
                    label: 'Email',
                },
                {
                    key: 'actions',
                    label: 'Actions',
                    class: 'text-center',
                }
            ]
        }
    },
    computed: {

    },
    mounted: function() {
        this.getInvitedTus();
    },
    methods: {
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        async getInvitedTus() {
            // fetches the original list of TUs who are invited_tus for this RfxResponse
            this.loading = true; 
            try {
                const invitedTusResp = await suppliosApi.rfxResponse.get_invited_tus(this.responseId);
                this.invitedTus = invitedTusResp.data;
            } catch (err) {
                this.handleError(err, 'Error while getting list of other invited users');
            }
            this.loading = false;
        },
        getCompanyTus: function() {
            // fetches the full list of TUs in the company so user can add/remove them from the RFX
            this.loadingAllTus = true;
            return suppliosApi.rfxResponse.getCompanyTuList(this.responseId)
                .then(response => {
                    this.allTus = response.data.all_tus;
                    //this.invitedTusIds = response.data.invited_tus;
                    //this.setInvitedTus(this.invitedTusIds);
                    // also returns response.data.invited_tus -- ids of invited_tus
                    return true;
                })
                .catch(err => {
                    this.handleError(err, 'Error while getting list of users in your company');
                    return false;
                })
                .then(success => {
                    this.loadingAllTus = false;
                    return success;
                });
        },
        setInvitedTus: function(invitedTuIdList) {
            // make a list of objects from our allTus list and IDs from invited_tus id list
            let invitedTuObjList = this.allTus.filter(tu => invitedTuIdList.includes(tu.id));
            this.invitedTus = invitedTuObjList;
        },
        addTu: function(tuId) {
            this.adding = true;
            return suppliosApi.rfxResponse.addInvitedTu(this.responseId, tuId)
                .then(response => {
                    // returns a list if invited_tus full objects
                    this.invitedTus = response.data;
                    //this.invitedTusIds = response.data.invited_tus;
                    //this.setInvitedTus(this.invitedTusIds);
                    return true;
                })
                .catch(err => {
                    this.handleError(err, 'Error while adding new Team Member');
                    return false;
                })
                .then(success => {
                    this.adding = false;
                    return success;
                });
        },
        isInvited: function(tuObj) {
            // return Boolean if this tuObj is in the invited_tus list
            return this.invitedTus.map(tu => tu.id).includes(tuObj.id);
        },
        removeTu: function(tuObj) {
            this.$bvModal.msgBoxConfirm(`Are you sure you want to remove access to this Sourcing Event for ${tuObj.full_name}?`, {
                title: 'Remove Access?',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Remove Access',
                cancelTitle: 'No, Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            })
            .then(value => {
                if (value) this.performRemoveTu(tuObj);
            });
        },
        performRemoveTu: function(tuObj) {
            this.removing = true;
            return suppliosApi.rfxResponse.removeInvitedTu(this.responseId, tuObj.id)
                .then(response => {
                    // returns a list if invited_tus full objects
                    this.invitedTus = response.data;
                    //this.invitedTusIds = response.data.invited_tus;
                    //this.setInvitedTus(this.invitedTusIds);
                    return true;
                })
                .catch(err => {
                    this.handleError(err, 'Error while removing Team Member');
                    return false;
                })
                .then(success => {
                    this.removing = false;
                    return success;
                });
        },
    }
}
</script>

<style>

</style>